import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MdArrowForward, MdChevronLeft, MdChevronRight, MdLocationOn } from "react-icons/md";

import { getLatestEvent } from "../EventsGallery/galleryEvents";
import "./gallery_home.css";

const SLIDE_INTERVAL = 3000;

const GalleryHome = () => {
  const latestEvent = getLatestEvent();
  const images = latestEvent?.images || [];
  const slideCount = images.length;

  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const goToSlide = useCallback(
    (index) => setActiveSlide(((index % slideCount) + slideCount) % slideCount),
    [slideCount]
  );

  useEffect(() => {
    if (isPaused || slideCount < 2) {
      return undefined;
    }

    const timer = setInterval(() => {
      setActiveSlide((current) => (current + 1) % slideCount);
    }, SLIDE_INTERVAL);

    return () => clearInterval(timer);
  }, [isPaused, slideCount]);

  if (!latestEvent) {
    return null;
  }

  return (
    <section id="gallery_home_section" aria-labelledby="gallery_home_hdng">
      <div id="gallery_home_inner">
        <div id="gallery_home_intro">
          <h2 id="gallery_home_hdng">Latest From The Gallery</h2>
          <Link id="gallery_home_view_all" to="/gallery">
            View Full Gallery
            <MdArrowForward aria-hidden="true" />
          </Link>
        </div>

        <article id="gallery_home_feature">
          <div
            id="gallery_home_slider"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            aria-label={`${latestEvent.title} photos`}
          >
            <div
              id="gallery_home_slider_track"
              style={{ transform: `translateX(-${activeSlide * 100}%)` }}
            >
              {images.map((image, index) => (
                <Link
                  className="gallery_home_slide"
                  key={`${latestEvent.id}-slide-${index}`}
                  to="/gallery"
                  tabIndex={index === activeSlide ? 0 : -1}
                  aria-hidden={index === activeSlide ? undefined : true}
                >
                  <img src={image} alt={`${latestEvent.title} ${index + 1}`} loading="lazy" />
                </Link>
              ))}
            </div>

            {slideCount > 1 && (
              <>
                <button
                  className="gallery_home_slider_arrow gallery_home_slider_arrow_prev"
                  type="button"
                  onClick={() => goToSlide(activeSlide - 1)}
                  aria-label="Previous photo"
                >
                  <MdChevronLeft aria-hidden="true" />
                </button>
                <button
                  className="gallery_home_slider_arrow gallery_home_slider_arrow_next"
                  type="button"
                  onClick={() => goToSlide(activeSlide + 1)}
                  aria-label="Next photo"
                >
                  <MdChevronRight aria-hidden="true" />
                </button>

                <div id="gallery_home_slider_dots">
                  {images.map((image, index) => (
                    <button
                      className={
                        index === activeSlide
                          ? "gallery_home_slider_dot gallery_home_slider_dot_active"
                          : "gallery_home_slider_dot"
                      }
                      key={`${latestEvent.id}-dot-${index}`}
                      type="button"
                      onClick={() => goToSlide(index)}
                      aria-label={`Go to photo ${index + 1}`}
                      aria-current={index === activeSlide}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          <div id="gallery_home_copy">
            <p id="gallery_home_meta">{latestEvent.date}</p>
            <h3 id="gallery_home_title">{latestEvent.title}</h3>
            <p id="gallery_home_location">
              <MdLocationOn aria-hidden="true" />
              <span>{latestEvent.location}</span>
            </p>
            <p id="gallery_home_desc">{latestEvent.description}</p>
            <Link id="gallery_home_cta" to="/gallery">
              View Event Photos
              <MdArrowForward aria-hidden="true" />
            </Link>
          </div>
        </article>
      </div>
    </section>
  );
};

export default GalleryHome;
